/*
 * "Add to Home Screen".
 *
 * The whole reason every business gets its own origin is that the app is
 * installable, and browsers do not reliably surface that on their own: Chrome
 * buries it in an overflow menu and Safari never offers it at all. So the
 * business's own app has to ask.
 *
 * THREE PLATFORMS, THREE BEHAVIOURS, and pretending otherwise is what makes
 * most install buttons broken:
 *
 *   Chrome / Edge / Android   fire `beforeinstallprompt`. Capturing it is the
 *                             only way to open the real install dialog later —
 *                             prompt() cannot be called out of the blue.
 *   iOS Safari                fires nothing and exposes no API. Installing is
 *                             Share -> Add to Home Screen, by hand, so the only
 *                             honest thing to offer is instructions.
 *   Everything else           cannot install. Show nothing, rather than a
 *                             button that does nothing.
 *
 * THE EVENT USUALLY FIRES BEFORE REACT MOUNTS. A listener added in an effect is
 * too late on a fast connection and in time on a slow one, which reads as
 * flakiness. main.tsx stashes the event on `window.__blooqInstall` before React
 * renders; this reads that first and only then subscribes for the late case.
 *
 * Inside the native app none of this applies — it is already installed, by
 * definition — so the whole feature is off there.
 */

import { useCallback, useEffect, useState } from 'react'

import { isNative } from './tenant'

export interface InstallEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

declare global {
  interface Window {
    __blooqInstall?: InstallEvent | null
  }
}

/** Per-origin, and every business is its own origin, so this is per-business. */
const DISMISS_KEY = 'blooq.install.dismissed'

/** Fired by main.tsx when the event lands after the component has mounted. */
export const INSTALLABLE_EVENT = 'blooq:installable'

/** Storage throws in private mode and in some in-app browsers. Never fatal. */
function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

function writeDismissed() {
  try {
    window.localStorage.setItem(DISMISS_KEY, '1')
  } catch {
    // A customer who cannot persist the dismissal sees the sheet again next
    // visit. Mildly annoying; not worth a failed render.
  }
}

/**
 * Already installed?
 *
 * Two checks, not one: `display-mode: standalone` covers Android and desktop,
 * and `navigator.standalone` is the iOS-only equivalent — an installed iOS app
 * still reports `browser` for display-mode.
 */
function isInstalled(): boolean {
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches
  const ios = (window.navigator as Navigator & { standalone?: boolean }).standalone
  return Boolean(standalone || ios)
}

/**
 * iOS Safari, and specifically not Chrome-on-iOS.
 *
 * Every iOS browser is WebKit underneath, but only Safari can add to the home
 * screen. Showing Safari's instructions inside Chrome sends the customer
 * hunting for a menu item that is not there.
 */
function isIosSafari(): boolean {
  const ua = navigator.userAgent
  const ios =
    /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  if (!ios) return false
  return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)
}

/** 'prompt' = a real install API. 'ios' = written instructions. */
export type InstallMode = 'hidden' | 'prompt' | 'ios'

export interface InstallState {
  mode: InstallMode
  /** The sheet offers itself once per origin; the button stays for good. */
  sheetOpen: boolean
  openSheet: () => void
  closeSheet: () => void
  /** Opens the browser's own dialog. Only meaningful when mode is 'prompt'. */
  install: () => void
}

/**
 * Long enough that the business's greeting is read first, short enough that the
 * sheet does not feel like an afterthought. A modal on first paint covers the
 * greeting before a word of it has been read, which is the pattern people close
 * reflexively.
 */
const APPEAR_AFTER_MS = 1600

export function useInstall(enabled: boolean): InstallState {
  const [event, setEvent] = useState<InstallEvent | null>(null)
  const [installed, setInstalled] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [offered, setOffered] = useState(false)

  useEffect(() => {
    if (!enabled || isNative) return

    setInstalled(isInstalled())
    setEvent(window.__blooqInstall ?? null)

    const onLate = () => setEvent(window.__blooqInstall ?? null)
    // Chrome fires this when the app is installed by any route, including its
    // own menu. Without it the button stays offering an install that already
    // happened.
    const onInstalled = () => {
      setInstalled(true)
      setEvent(null)
      setSheetOpen(false)
    }

    window.addEventListener(INSTALLABLE_EVENT, onLate)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener(INSTALLABLE_EVENT, onLate)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [enabled])

  const mode: InstallMode =
    !enabled || isNative || installed ? 'hidden' : event ? 'prompt' : isIosSafari() ? 'ios' : 'hidden'

  // Offer the sheet once, a beat after the greeting has landed.
  useEffect(() => {
    if (mode === 'hidden' || offered || readDismissed()) return
    const timer = window.setTimeout(() => {
      setOffered(true)
      setSheetOpen(true)
    }, APPEAR_AFTER_MS)
    return () => window.clearTimeout(timer)
  }, [mode, offered])

  const closeSheet = useCallback(() => {
    setSheetOpen(false)
    // Dismissing is "not now", not "never": the header button stays for as long
    // as the app is installable, so a customer who waved this away on a first
    // visit can still find it later.
    writeDismissed()
  }, [])

  const install = useCallback(() => {
    const pending = event
    if (!pending) return
    setSheetOpen(false)
    void pending.prompt().then(() => {
      // The event is single-use. Clearing it is what stops a second tap
      // silently doing nothing.
      window.__blooqInstall = null
      setEvent(null)
    })
  }, [event])

  return {
    mode,
    sheetOpen,
    openSheet: useCallback(() => setSheetOpen(true), []),
    closeSheet,
    install,
  }
}
